import React from "react";
import { ArticleInfoProps } from "../../types/article-info";
import { PriceType } from "../../types/articles";
import { publishToOrderModal } from "../../util/call-order-modal";
import AvailableColorsIcon from "../../assets/available-colors-icon";
import "./article-info.scss";

const ArticleInfo = (props: ArticleInfoProps) => {
  const OVERSIZED_PRICE_LENGTH: number = 24;
  const SLIGHTLY_OVERSIZED_PRICE_LENGTH: number = 18;
  const priceInfo = props.article.priceInformation;

  const handleClick = () =>
    publishToOrderModal(
      props.article,
      props.itemOrigin,
      props.productRecoModel,
      props.pageLocation
    );

  const showDiscountPrices =
    !!priceInfo.discountPrice && !props.article.salesInformation.flagAdvertisedAsCostFreeInSet;
  const showAvailableColors =
    props.article.availableColors >= 1 && props.article.availableModels == 1;
  const pricePrefix =
    props.article.priceInformation.priceType === PriceType.From
      ? `${props.l10n.priceFromLocale} `
      : "";
  const showPriceAnnotation = priceInfo.minimalPrice.quantity > 1;
  const priceAnnotation = `${pricePrefix}${priceInfo.minimalPrice.quantity} ${props.article.packagingInformation.quantityUnitPlural}`;

  const getMainPrice = (priceValue: string) => {
    const priceString = props.currencyInformation.prependCurrencySymbol
      ? `${pricePrefix}${props.currencyInformation.symbol} ${priceValue}`
      : `${pricePrefix}${priceValue} ${props.currencyInformation.symbol}`;
    return priceString;
  };

  const minimalPriceGross = getMainPrice(priceInfo.minimalPrice.price.grossValue);
  const minimalPriceNet = getMainPrice(priceInfo.minimalPrice.price.netValue);
  const strikePriceGross = showDiscountPrices
    ? getMainPrice(priceInfo.discountPrice!.price.grossValue)
    : "";
  const strikePriceNet = showDiscountPrices
    ? getMainPrice(priceInfo.discountPrice!.price.netValue)
    : "";

  const mainPriceGrossLength = strikePriceGross.length + minimalPriceGross.length;
  const mainPriceNetLength = strikePriceNet.length + minimalPriceNet.length;

  const getOversizedPriceClass = (mainPriceLength: number) => {
    if (mainPriceLength > OVERSIZED_PRICE_LENGTH) {
      return "oversized";
    } else if (mainPriceLength > SLIGHTLY_OVERSIZED_PRICE_LENGTH) {
      return "slightly-oversized";
    } else return "normal";
  };

  const oversizedPriceGrossClass = getOversizedPriceClass(mainPriceGrossLength);
  const oversizedPriceNetClass = getOversizedPriceClass(mainPriceNetLength);

  const availableString = (isColorString: boolean, count: number) => {
    return isColorString
      ? count > 1
        ? `${count} ${props.l10n.colors}`
        : `${count} ${props.l10n.color}`
      : count > 1
        ? `${count} ${props.l10n.variants}`
        : `${count} ${props.l10n.variant}`;
  };

  const comparisonPrice = (priceValue: string) => {
    const formattedPrice = props.currencyInformation.prependCurrencySymbol
      ? `${props.currencyInformation.symbol} ${priceValue}`
      : `${priceValue} ${props.currencyInformation.symbol}`;
    return `${formattedPrice}/${priceInfo.comparisonPrice?.unit}`;
  };

  return (
    <div
      className="article-info"
      data-semantic-id="product-recommendation-tile"
      data-product-recommendation-tile-index={props.article.slot}
      data-product-recommendation-fragment={props.dataFragmentName}
      data-product-recommendation-model={props.productRecoModel}
      onClick={handleClick}
    >
      <span className="article-sales-designation">{props.article.salesDesignation}</span>

      <div className="price-model-container">
        <div className="article-prices gross">
          <div className={`article-main-price ${oversizedPriceGrossClass}`}>
            {showDiscountPrices && (
              <>
                <div className="strike-price gross">
                  <span className="price-value notranslate">{strikePriceGross}</span>
                </div>
                <span className="price-spacer">&nbsp;</span>
              </>
            )}
            <span className={`price-value ${showDiscountPrices ? "discounted" : ""} notranslate`}>
              {minimalPriceGross}
            </span>
          </div>

          {priceInfo.comparisonPrice && (
            <div className="comparison-price gross">
              <span className="price-prefix">
                {props.l10n.comparisonPrice}
                {props.l10n.colon}{" "}
              </span>
              <span className="value notranslate">
                {comparisonPrice(priceInfo.comparisonPrice.price.grossValue)}
              </span>
            </div>
          )}

          <span className="price-context">
            <span className="vat-label">({props.l10n.incVATAcronym})</span>{" "}
            {showPriceAnnotation && <span className="price-annotation">{priceAnnotation}</span>}
          </span>
        </div>

        <div className="article-prices net">
          <div className={`article-main-price ${oversizedPriceNetClass}`}>
            {showDiscountPrices && (
              <>
                <div className="strike-price net">
                  <span className="price-value notranslate">{strikePriceNet}</span>
                </div>
                <span className="price-spacer">&nbsp;</span>
              </>
            )}
            <span className={`price-value ${showDiscountPrices ? "discounted" : ""} notranslate`}>
              {minimalPriceNet}
            </span>
          </div>

          {priceInfo.comparisonPrice && (
            <div className="comparison-price net">
              <span className="price-prefix">
                {props.l10n.comparisonPrice}
                {props.l10n.colon}{" "}
              </span>
              <span className="value notranslate">
                {comparisonPrice(priceInfo.comparisonPrice.price.netValue)}
              </span>
            </div>
          )}

          <span className="price-context">
            <span className="vat-label">({props.l10n.exVATAcronym})</span>{" "}
            {showPriceAnnotation && <span className="price-annotation">{priceAnnotation}</span>}
          </span>
        </div>

        {showAvailableColors ? (
          <div className="available-colors">
            <AvailableColorsIcon />{" "}
            <span>{availableString(true, props.article.availableColors)}</span>
          </div>
        ) : (
          <div className="available-models">
            <span>{availableString(false, props.article.availableModels)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleInfo;
